import { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit3, Trash2, Loader, Check, X, Tag, Sparkles, ImageIcon, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authed = () => {
  const t = localStorage.getItem("admin_session_token");
  return { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const EMPTY = {
  title: "",
  kicker: "Monsoon Wellness Special",
  description: "",
  service_name: "",
  duration: "90 min",
  original_price: 0,
  offer_price: 0,
  inclusions: [],
  image: "",
  cta_label: "Book Monsoon Offer",
  start_date: "",
  end_date: "",
  active: true,
};

function AiImagePanel({ onPick }) {
  const [prompt, setPrompt] = useState(
    "A warm, candle-lit spa treatment room with soft golden light, luxurious draped linens, " +
    "aromatic essential oils on a wooden tray, monsoon rain softly visible through the window. " +
    "Cinematic wellness mood. 16:9 marketing hero.",
  );
  const [model, setModel] = useState("flash");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [recent, setRecent] = useState([]);

  async function load() {
    try {
      const r = await axios.get(`${API}/admin/ai/images?limit=8`, authed());
      setRecent(r.data.images || []);
    } catch (err) {
      console.error("Failed to load recent AI images:", err);
    }
  }
  useEffect(() => { load(); }, []);

  async function generate(e) {
    e?.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/ai/generate-image`,
        { prompt, model, tag: "offer" },
        authed(),
      );
      // Prepend latest image for immediate preview.
      setRecent((prev) => [{ ...r.data, prompt, created_at: new Date().toISOString() }, ...prev].slice(0, 8));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not generate the image. Try a different prompt.");
    } finally {
      setBusy(false);
    }
  }

  async function copyPath(url) {
    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      console.error("Could not copy image URL to clipboard:", err);
    }
  }

  return (
    <section className="ai-image-panel" data-testid="ai-image-panel">
      <header>
        <div>
          <p className="eyebrow"><Sparkles size={13} /> Nano Banana image generator</p>
          <h3 style={{ margin: "4px 0 0", color: "var(--forest)" }}>Generate marketing imagery</h3>
          <p className="muted-small">
            Describe the mood of the image you want. The result is saved and ready to drop into any offer.
          </p>
        </div>
      </header>
      <form onSubmit={generate} className="ai-image-form">
        <label>
          Prompt
          <textarea
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            maxLength={1000}
            placeholder="A warm, candle-lit spa room with monsoon rain softly visible through the window…"
            data-testid="ai-image-prompt"
          />
        </label>
        <div className="ai-image-controls">
          <label className="model-pick">
            Model
            <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="ai-image-model">
              <option value="flash">Gemini 3.1 Flash (Nano Banana, fastest)</option>
              <option value="pro">Gemini 3 Pro Image (highest quality)</option>
            </select>
          </label>
          <button type="submit" className="btn btn-primary" disabled={busy || prompt.trim().length < 10} data-testid="ai-image-generate">
            {busy ? <><Loader size={14} className="spin" /> Generating…</> : <><ImageIcon size={14} /> Generate image</>}
          </button>
        </div>
        {err && <p className="form-error" data-testid="ai-image-error">{err}</p>}
      </form>

      {recent.length > 0 && (
        <div className="ai-image-gallery" data-testid="ai-image-gallery">
          {recent.map((img, i) => (
            <figure key={img.storage_path || i} className="ai-image-tile">
              <img src={img.url} alt={img.prompt?.slice(0, 80) || "generated"} loading="lazy" />
              <figcaption>
                <p className="muted-small">{img.prompt?.slice(0, 110)}{(img.prompt?.length || 0) > 110 ? "…" : ""}</p>
                <div className="actions-row" style={{ margin: 0, gap: 6 }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => onPick(img.url)}
                    data-testid={`ai-image-use-${i}`}
                  >
                    <Check size={13} /> Use in new offer
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => copyPath(img.url)}
                    data-testid={`ai-image-copy-${i}`}
                    title="Copy image URL"
                  >
                    <Copy size={13} /> Copy URL
                  </button>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}

export default function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // offer id or 'new'
  const [draft, setDraft] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/offers`, authed());
      setOffers(r.data.offers || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load offers.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function startNew() {
    setDraft(EMPTY);
    setEditing("new");
    setErr("");
  }
  function startEdit(o) {
    setDraft({
      title: o.title,
      kicker: o.kicker,
      description: o.description,
      service_name: o.service_name,
      duration: o.duration,
      original_price: o.original_price,
      offer_price: o.offer_price,
      inclusions: o.inclusions || [],
      image: o.image || "",
      cta_label: o.cta_label || "Book Monsoon Offer",
      start_date: o.start_date,
      end_date: o.end_date,
      active: !!o.active,
    });
    setEditing(o.id);
    setErr("");
  }
  function cancel() {
    setEditing(null);
    setDraft(EMPTY);
    setErr("");
  }

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const payload = {
        ...draft,
        original_price: Number(draft.original_price),
        offer_price: Number(draft.offer_price),
        inclusions: typeof draft.inclusions === "string"
          ? draft.inclusions.split("\n").map((s) => s.trim()).filter(Boolean)
          : draft.inclusions,
      };
      if (editing === "new") {
        await axios.post(`${API}/admin/offers`, payload, authed());
      } else {
        await axios.put(`${API}/admin/offers/${editing}`, payload, authed());
      }
      cancel();
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this offer? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/offers/${id}`, authed());
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed.");
    }
  }

  async function toggleActive(o) {
    try {
      await axios.put(
        `${API}/admin/offers/${o.id}`,
        {
          ...o,
          active: !o.active,
          inclusions: o.inclusions || [],
        },
        authed(),
      );
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not toggle.");
    }
  }

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setDraft({ ...draft, [k]: v });
  };

  return (
    <div data-testid="admin-offers">
      <p className="eyebrow"><Tag size={13} /> Monsoon &amp; seasonal offers</p>
      <h1>Offers</h1>
      <p className="muted">Create, pause or edit the promotional offers shown on the public site. The first active offer appears in the home-page strip.</p>

      {err && <p className="form-error">{err}</p>}

      <AiImagePanel
        onPick={(url) => {
          setDraft({ ...EMPTY, image: url });
          setEditing("new");
          setErr("");
          // scroll into view so the admin sees the form appear
          setTimeout(() => {
            document.querySelector('[data-testid="admin-offer-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 50);
        }}
      />

      <div className="admin-toolbar">
        <button className="btn btn-primary" onClick={startNew} data-testid="admin-offer-new">
          <Plus size={14} /> New offer
        </button>
      </div>

      {loading ? (
        <p className="muted"><Loader size={14} className="spin" /> Loading…</p>
      ) : offers.length === 0 ? (
        <p className="muted">No offers yet. Click "New offer" to create one.</p>
      ) : (
        <table className="admin-table" data-testid="admin-offers-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Service</th>
              <th>Window</th>
              <th>Price</th>
              <th>Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {offers.map((o) => (
              <tr key={o.id}>
                <td><strong>{o.title}</strong><br /><span className="muted-small">{o.kicker}</span></td>
                <td>{o.service_name}<br /><span className="muted-small">{o.duration}</span></td>
                <td>{o.start_date} → {o.end_date}</td>
                <td>₹{o.offer_price.toLocaleString("en-IN")}<br /><s className="muted-small">₹{o.original_price.toLocaleString("en-IN")}</s></td>
                <td>
                  <button
                    type="button"
                    onClick={() => toggleActive(o)}
                    className={`toggle-pill ${o.active ? "on" : "off"}`}
                    data-testid={`admin-offer-toggle-${o.id}`}
                  >
                    {o.active ? <><Check size={11} /> Live</> : <><X size={11} /> Paused</>}
                  </button>
                </td>
                <td className="row-actions">
                  <button type="button" className="icon-btn" onClick={() => startEdit(o)} aria-label="Edit" data-testid={`admin-offer-edit-${o.id}`}>
                    <Edit3 size={14} />
                  </button>
                  <button type="button" className="icon-btn danger" onClick={() => remove(o.id)} aria-label="Delete" data-testid={`admin-offer-delete-${o.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <form onSubmit={save} className="seo-form admin-form" data-testid="admin-offer-form" style={{ marginTop: 28 }}>
          <h3 style={{ margin: 0, color: "var(--forest)" }}>{editing === "new" ? "Create offer" : "Edit offer"}</h3>
          <div className="form-grid-2">
            <label>Title <input required value={draft.title} onChange={update("title")} /></label>
            <label>Kicker <input value={draft.kicker} onChange={update("kicker")} /></label>
            <label>Service name <input required value={draft.service_name} onChange={update("service_name")} /></label>
            <label>Duration <input required value={draft.duration} onChange={update("duration")} placeholder="90 min" /></label>
            <label>Original price (₹) <input required type="number" min="1" value={draft.original_price} onChange={update("original_price")} /></label>
            <label>Offer price (₹) <input required type="number" min="1" value={draft.offer_price} onChange={update("offer_price")} /></label>
            <label>Start date <input required type="date" value={draft.start_date} onChange={update("start_date")} /></label>
            <label>End date <input required type="date" value={draft.end_date} onChange={update("end_date")} /></label>
            <label>Image URL (optional) <input value={draft.image} onChange={update("image")} placeholder="/images/zorba/..." /></label>
            <label>CTA label <input value={draft.cta_label} onChange={update("cta_label")} /></label>
          </div>
          <label>Description <textarea rows={3} required value={draft.description} onChange={update("description")} /></label>
          <label>
            Inclusions (one per line)
            <textarea
              rows={5}
              value={Array.isArray(draft.inclusions) ? draft.inclusions.join("\n") : draft.inclusions}
              onChange={(e) => setDraft({ ...draft, inclusions: e.target.value })}
            />
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={draft.active} onChange={update("active")} /> Active
          </label>
          <div className="actions-row">
            <button type="submit" className="btn btn-primary" disabled={busy} data-testid="admin-offer-save">
              {busy ? <><Loader size={14} className="spin" /> Saving…</> : "Save"}
            </button>
            <button type="button" className="btn btn-secondary" onClick={cancel}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}
